import React, { useState } from 'react';

import {
  Box,
  ClickAwayListener,
  IconButton,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { makeStyles } from 'tss-react/mui';

interface IAppInfoTooltipButtonProps {
  text?: string;
  content?: React.ReactNode;
  placement?: TooltipProps['placement'];
  icon?: React.ReactNode;
  className?: string;
}

const useStyles = makeStyles<Theme>()((theme) => ({
  tooltip: {
    maxWidth: '280px',
    whiteSpace: 'normal',
    lineHeight: 1.5,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    boxSizing: 'border-box',
    borderRadius: theme.spacing(1.25),
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5),
  },
  button: {
    color: theme.palette.warning.main,
    padding: theme.spacing(0.5),
    '& svg': {
      fontSize: 18,
    },
  },
  tooltipText: {
    whiteSpace: 'pre-wrap',
  },
}));

const AppInfoTooltipButton: React.FC<IAppInfoTooltipButtonProps> = ({
  text,
  content,
  placement = 'top',
  icon,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { classes, cx } = useStyles(theme);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <Tooltip
          placement={placement}
          onClose={() => setOpen(false)}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          classes={{ tooltip: classes.tooltip }}
          title={
            content ?? (
              <Typography variant="text-sm" className={classes.tooltipText}>
                {text ?? ''}
              </Typography>
            )
          }>
          <IconButton
            size="small"
            className={cx(classes.button, className)}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}>
            {icon ?? <ErrorOutlineIcon />}
          </IconButton>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
};

export default AppInfoTooltipButton;
